import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Loader2, Trash2, Edit3 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { operacionesApi } from '../../../lib/api';

const dias = [
  { v: 1, n: 'Lun' }, { v: 2, n: 'Mar' }, { v: 3, n: 'Mié' },
  { v: 4, n: 'Jue' }, { v: 5, n: 'Vie' }, { v: 6, n: 'Sáb' }, { v: 0, n: 'Dom' },
];

const empty = {
  nombre: '', capacidad: 8, descripcion: '',
  horario_inicio: '08:00', horario_fin: '22:00',
  dias_disponibles: [1, 2, 3, 4, 5],
  granularidad_min: 60, antelacion_cancelacion_min: 120, activa: true,
};

export default function OperacionesSalas({ edificioId }) {
  const [salas, setSalas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await operacionesApi.listSalas(edificioId);
      setSalas(r.data || []);
    } catch { toast.error('Error al cargar salas'); }
    finally { setLoading(false); }
  }, [edificioId]);

  useEffect(() => { reload(); }, [reload]);

  const openNew = () => { setForm(empty); setDialog({ mode: 'new' }); };
  const openEdit = (s) => {
    setForm({
      nombre: s.nombre, capacidad: s.capacidad, descripcion: s.descripcion || '',
      horario_inicio: s.horario_inicio, horario_fin: s.horario_fin,
      dias_disponibles: s.dias_disponibles || [],
      granularidad_min: s.granularidad_min, antelacion_cancelacion_min: s.antelacion_cancelacion_min,
      activa: s.activa,
    });
    setDialog({ mode: 'edit', target: s });
  };
  const close = () => { setDialog(null); setForm(empty); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { edificio_id: edificioId, ...form };
      if (dialog.mode === 'new') await operacionesApi.createSala(payload);
      else await operacionesApi.updateSala(dialog.target.sala_id, payload);
      toast.success('Sala guardada');
      close();
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!confirm(`¿Eliminar la sala "${s.nombre}"?`)) return;
    try {
      await operacionesApi.deleteSala(s.sala_id);
      toast.success('Eliminada');
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const toggleDia = (v) => setForm(f => ({
    ...f,
    dias_disponibles: f.dias_disponibles.includes(v)
      ? f.dias_disponibles.filter(x => x !== v)
      : [...f.dias_disponibles, v].sort()
  }));

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">Salas del edificio. Configurá horarios, capacidad y granularidad.</div>
        <Button size="sm" onClick={openNew} data-testid="btn-new-sala">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nueva sala
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Sala</th>
              <th className="text-right px-3 py-2 font-medium">Cap.</th>
              <th className="text-left px-3 py-2 font-medium">Horario</th>
              <th className="text-left px-3 py-2 font-medium">Días</th>
              <th className="text-right px-3 py-2 font-medium">Bloque</th>
              <th className="text-right px-3 py-2 font-medium">Cancelación</th>
              <th className="text-center px-3 py-2 font-medium">Activa</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {salas.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">Sin salas</td></tr>}
            {salas.map(s => (
              <tr key={s.sala_id} className="hover:bg-slate-50/40">
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{s.nombre}</div>
                  {s.descripcion && <div className="text-[11px] text-slate-500">{s.descripcion}</div>}
                </td>
                <td className="px-3 py-2 text-right">{s.capacidad}</td>
                <td className="px-3 py-2">{s.horario_inicio}–{s.horario_fin}</td>
                <td className="px-3 py-2 text-xs">{s.dias_disponibles.map(v => dias.find(d => d.v === v)?.n).join(' ')}</td>
                <td className="px-3 py-2 text-right">{s.granularidad_min}'</td>
                <td className="px-3 py-2 text-right">{s.antelacion_cancelacion_min}'</td>
                <td className="px-3 py-2 text-center">{s.activa ? '✓' : '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => openEdit(s)} className="p-1 hover:bg-slate-100 rounded"><Edit3 className="w-3.5 h-3.5 text-slate-500" /></button>
                  <button onClick={() => del(s)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={o => !o && close()}>
        <DialogContent className="max-w-xl">
          <form onSubmit={submit}>
            <DialogHeader><DialogTitle>{dialog?.mode === 'new' ? 'Nueva sala' : 'Editar sala'}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="col-span-2"><Label>Nombre *</Label><Input required value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} /></div>
              <div><Label>Capacidad *</Label><Input type="number" min={1} required value={form.capacidad} onChange={e => setForm({...form, capacidad: parseInt(e.target.value) || 0})} /></div>
              <div><Label>Bloque (min)</Label><Input type="number" min={15} step={15} value={form.granularidad_min} onChange={e => setForm({...form, granularidad_min: parseInt(e.target.value) || 60})} /></div>
              <div><Label>Horario inicio</Label><Input type="time" value={form.horario_inicio} onChange={e => setForm({...form, horario_inicio: e.target.value})} /></div>
              <div><Label>Horario fin</Label><Input type="time" value={form.horario_fin} onChange={e => setForm({...form, horario_fin: e.target.value})} /></div>
              <div className="col-span-2"><Label>Cancelación con antelación (min)</Label><Input type="number" min={0} value={form.antelacion_cancelacion_min} onChange={e => setForm({...form, antelacion_cancelacion_min: parseInt(e.target.value) || 0})} /></div>
              <div className="col-span-2">
                <Label>Días disponibles</Label>
                <div className="flex gap-1 mt-1">
                  {dias.map(d => (
                    <button key={d.v} type="button"
                      onClick={() => toggleDia(d.v)}
                      className={`flex-1 px-2 py-1 text-xs rounded border ${form.dias_disponibles.includes(d.v) ? 'bg-amber-100 border-amber-400 text-amber-900' : 'bg-white border-slate-200 text-slate-500'}`}>
                      {d.n}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2"><Label>Descripción</Label><Input value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} /></div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="activa-sala" checked={form.activa} onChange={e => setForm({...form, activa: e.target.checked})} />
                <Label htmlFor="activa-sala">Sala activa</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
